/**
 * <s-pair-bundle> — "Buy it as a pair" on product pages: this product + a chosen tailored trouser.
 *
 * The markup is a working native form (posts items[0][id] and items[1][id] to the cart), so it adds
 * both pieces even without JS. This element enhances it: trouser thumbnails switch the shown trouser,
 * its size select and photo; the pair total updates; submit adds both lines via Shopify Standard
 * Actions and opens the cart drawer.
 *
 * Expected DOM (templates/product.liquid, "BUY IT AS A PAIR"):
 *   <s-pair-bundle data-money-format data-error-message>
 *     <form> … <select data-pair-size="main"> … <input type="radio" data-pair-choice value="ID"> …
 *       <div data-pair-option="ID"><select data-pair-size="trouser"></div> … <img wrapper data-pair-photo="ID">
 *       <span data-pair-total> … <button name="add"> … <p data-pair-error>
 */
class SPairBundle extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.ac = new AbortController();
    const opts = { signal: this.ac.signal };

    this.form.addEventListener('change', (event) => {
      if (event.target.matches('[data-pair-choice]')) this.choose(event.target.value);
      this.updateTotal();
    }, opts);

    this.form.addEventListener('submit', (event) => {
      if (!window.Shopify?.actions?.updateCart) return; // native post fallback
      event.preventDefault();
      this.submit(event.submitter);
    }, opts);

    const checked = this.form.querySelector('[data-pair-choice]:checked');
    if (checked) this.choose(checked.value);
    this.updateTotal();
  }

  disconnectedCallback() {
    this.ac?.abort();
  }

  choose(id) {
    this.querySelectorAll('[data-pair-option]').forEach((el) => {
      const active = el.dataset.pairOption === id;
      el.hidden = !active;
      el.querySelectorAll('select').forEach((select) => { select.disabled = !active; });
    });
    this.querySelectorAll('[data-pair-photo]').forEach((el) => {
      el.classList.toggle('opacity-0', el.dataset.pairPhoto !== id);
      el.toggleAttribute('aria-hidden', el.dataset.pairPhoto !== id);
    });
  }

  selectedPrice(select) {
    const option = select?.selectedOptions?.[0];
    return option ? Number(option.dataset.price || 0) : 0;
  }

  updateTotal() {
    const main = this.querySelector('[data-pair-size="main"]');
    const trouser = this.querySelector('[data-pair-option]:not([hidden]) [data-pair-size="trouser"]');
    const total = this.selectedPrice(main) + this.selectedPrice(trouser);
    const out = this.querySelector('[data-pair-total]');
    if (out && total > 0) out.textContent = this.formatMoney(total);

    const button = this.form.querySelector('button[name="add"]');
    const unavailable = [main, trouser].some((s) => !s || s.selectedOptions[0]?.disabled);
    if (button) button.toggleAttribute('disabled', unavailable);
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const value = (cents / 100);
    const group = (n, decimals, thousands, decimal) => {
      const [whole, frac] = n.toFixed(decimals).split('.');
      const withSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${withSep}${decimal}${frac}` : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        default: return group(value, 2, ',', '.');
      }
    });
  }

  async submit(submitter) {
    if (this.getAttribute('aria-busy') === 'true') return;
    const lines = [...this.form.querySelectorAll('select[data-pair-size]:not(:disabled)')]
      .map((select) => ({ merchandiseId: String(select.value), quantity: 1 }))
      .filter((line) => line.merchandiseId);
    if (lines.length < 2) return;

    const error = this.querySelector('[data-pair-error]');
    if (error) { error.hidden = true; error.textContent = ''; }
    this.setAttribute('aria-busy', 'true');
    submitter?.classList.add('opacity-50', 'cursor-wait');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines },
        { event: { context: 'product', detail: { source: 's-pair-bundle' } } }
      );
      const message = result?.userErrors?.[0]?.message;
      if (message) throw new Error(message);
      await window.Shopify.actions.openCart?.();
    } catch (err) {
      if (error) {
        error.textContent = err?.message || this.dataset.errorMessage || '';
        error.hidden = false;
      }
    } finally {
      this.removeAttribute('aria-busy');
      submitter?.classList.remove('opacity-50', 'cursor-wait');
    }
  }
}

if (!customElements.get('s-pair-bundle')) {
  customElements.define('s-pair-bundle', SPairBundle);
}
